/**
 * B-GTCC-SVC-RULES-WIRE1 — shared loading/failure/freshness pieces for the
 * `GtccResult`-based Cockpit tabs (`SvcTab.tsx`, `RulesTab.tsx`). Same recipe
 * as `InfraTab.tsx`'s `FleetGate`/`InfraFreshBadge`, on `GtccResult`
 * (`../../../api/gtcc-result.ts`) instead of `FleetResult`; the loading,
 * failure and freshness copy is reused verbatim from
 * `admin.gtcc.cockpit.infra.*` — the wording is generic, not infra-specific.
 */
import { EmptyState, ErrorState } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import type { UseQueryResult } from "@tanstack/react-query";
import type { ReactNode } from "react";

import type { GtccResult } from "../../../api/gtcc-result.js";
import { FreshBadge } from "../../../ui/primitives.js";

export function CockpitNotMeasurable(): ReactNode {
  const { t } = useLocale();
  return <span className="mono muted">{t("admin.gtcc.cockpit.infra.not_measurable")}</span>;
}

export function CockpitFreshBadge(props: {
  readonly seconds: number | undefined;
  readonly measuredAt: string;
  readonly stale: boolean;
}): ReactNode {
  const { t, locale } = useLocale();
  if (props.seconds === undefined && Number.isNaN(new Date(props.measuredAt).getTime()))
    return null;
  const label =
    props.seconds === undefined
      ? t("admin.gtcc.cockpit.infra.fresh.measured_at", {
          time: new Intl.DateTimeFormat(locale, { timeStyle: "medium" }).format(
            new Date(props.measuredAt),
          ),
        })
      : props.seconds >= 60
        ? t("admin.gtcc.cockpit.infra.fresh.minutes", {
            minutes: new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(
              props.seconds / 60,
            ),
          })
        : t("admin.gtcc.cockpit.infra.fresh.seconds", { seconds: String(props.seconds) });
  return <FreshBadge label={label} stale={props.stale} />;
}

function failureBody(
  failure: Extract<GtccResult<unknown>, { ok: false }>,
  t: (key: string, vars?: Record<string, string | number>) => string,
): string {
  if (failure.kind === "disabled") return t("admin.gtcc.cockpit.infra.failure.disabled");
  if (failure.kind === "forbidden") return t("admin.gtcc.cockpit.infra.failure.forbidden");
  if (failure.kind === "upstream")
    return t("admin.gtcc.cockpit.infra.failure.upstream", { status: failure.httpStatus });
  return t("admin.gtcc.cockpit.infra.failure.transport");
}

/** A failed or pending read never falls back to stale numbers, it says what happened. */
export function CockpitGate<T>(props: {
  readonly query: UseQueryResult<GtccResult<T>>;
  readonly testId: string;
  readonly render: (data: T) => ReactNode;
}): ReactNode {
  const { t } = useLocale();
  const { query } = props;

  if (query.isPending || query.data === undefined) {
    return (
      <div data-testid={`${props.testId}-loading`}>
        <EmptyState title={t("admin.gtcc.cockpit.infra.loading")} />
      </div>
    );
  }
  if (query.data.ok) return props.render(query.data.data);
  return (
    <div data-testid={`${props.testId}-failure`}>
      <ErrorState
        severity="warn"
        title={t("admin.gtcc.cockpit.infra.failure.title")}
        body={failureBody(query.data, t)}
      />
    </div>
  );
}

/** Table-row shaped variant of `CockpitGate`: a `<tbody>` may only hold `<tr>`. */
export function CockpitTableGate<T>(props: {
  readonly query: UseQueryResult<GtccResult<T>>;
  readonly testId: string;
  readonly columns: number;
  readonly render: (data: T) => ReactNode;
}): ReactNode {
  const { t } = useLocale();
  const { query } = props;

  if (query.isPending || query.data === undefined) {
    return (
      <tr>
        <td colSpan={props.columns} data-testid={`${props.testId}-loading`}>
          <EmptyState title={t("admin.gtcc.cockpit.infra.loading")} />
        </td>
      </tr>
    );
  }
  if (query.data.ok) return <>{props.render(query.data.data)}</>;
  return (
    <tr>
      <td colSpan={props.columns} data-testid={`${props.testId}-failure`}>
        <ErrorState
          severity="warn"
          title={t("admin.gtcc.cockpit.infra.failure.title")}
          body={failureBody(query.data, t)}
        />
      </td>
    </tr>
  );
}
